import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/Context/UserContext";
import { useColor } from "@/Context/ColorContext";
import InputTextField from "@/Components/Form/InputTextField";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { BackLink } from "@/Components/Buttons/Link";
import { FaArrowLeft } from "@/Components/Icons/Icons";
import { navurl } from "@/Config/Routeconfig/NavigationUrl";
import { useResetPasswordMutation } from "@/redux/ForgotPassword/SetNewPassword";

function SetNewPassword() {
  const navigate = useNavigate();
  const { setUsername } = useUser();
  const { color } = useColor();

  const [formValues, setFormValues] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const handleChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNewPassword = async () => {
    const { newPassword, confirmPassword } = formValues;
    const errors = {};

    // Field-specific validation
    if (!newPassword.trim()) errors.newPassword = "Create New Password is required.";
    if (!confirmPassword.trim()) errors.confirmPassword = "Confirm New Password is required.";
    if (newPassword && confirmPassword && newPassword !== confirmPassword)
      errors.confirmPassword = "Passwords do not match.";
    if (newPassword && newPassword.length < 8)
      errors.newPassword = "Password must be at least 8 characters.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const userId = localStorage.getItem("userId");
    const sessionId = localStorage.getItem("sessionId");

    if (!userId || !sessionId) {
      setFieldErrors({ newPassword: "Session expired. Please start again." });
      return;
    }

    setFieldErrors({});
    setSuccessMessage("");

    try {
      const response = await resetPassword({ userId, newPassword, sessionId }).unwrap();

      if (response?.statusCode === 200) {
        setSuccessMessage(response.message);
        navigate(navurl.PasswordSuccess);

        // Clear sensitive data
        ["userId", "sessionId", "phone", "userName", "userToken"].forEach((key) =>
          localStorage.removeItem(key)
        );
      }
    } catch (err) {
      setFieldErrors({
        newPassword: err?.data?.message || "Error resetting password.",
      });
    }
  };

  return (
    <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="heading">Set New Password</Typography>
      <Typography variant="bodySmall" color="text.secondary" mb={3}>
        Password must be at least 8 characters
      </Typography>

      {/* New Password */}
      <InputTextField
        label="Create New Password"
        placeholder="***********"
        type="password"
        showToggle
        value={formValues.newPassword}
        onChange={(e) => handleChange("newPassword", e.target.value)}
        required
        error={!!fieldErrors.newPassword}
        helperText={fieldErrors.newPassword}
        width="100%"
      />

      {/* Confirm Password */}
      <InputTextField
        label="Confirm New Password"
        placeholder="***********"
        type="password"
        showToggle
        value={formValues.confirmPassword}
        onChange={(e) => handleChange("confirmPassword", e.target.value)}
        required
        error={!!fieldErrors.confirmPassword}
        helperText={fieldErrors.confirmPassword}
        width="100%"
        sx={{ mt: 1 }}
      />

      {/* Reset Password Button */}
      <BlackButton
        label={isLoading ? "Resetting..." : "Reset Password"}
        onClick={handleNewPassword}
        fullWidth
        disabled={isLoading}
        width= {"100%"}
        sx={{ mt: 2, mb: 2 }}
      />

      {/* Back to Sign In */}
      <BackLink
        label="Back to Sign In"
        icon={FaArrowLeft}
        iconPosition="front"
        navigateTo={navurl.Login}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          gap: 1,
          width: "100%",
        }}
      />

      {/* Success Message */}
      {successMessage && (
        <Typography color="success.main" mt={1} textAlign="center">
          {successMessage}
        </Typography>
      )}

     
    </Box>
  );
}

export default SetNewPassword;
